import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="bg-[#111111] py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF3B3B] text-sm font-semibold uppercase tracking-widest mb-4 block">
            Testimonials
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What Brands Say
          </h2>
          <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
            Don't just take my word for it—hear from the brands I've worked with.
          </p>
        </div>

        {/* Carousel */}
        <div className={`relative max-w-4xl mx-auto ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <Card className="bg-[#1A1A1A] border-white/5 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-6 left-6 opacity-10">
              <Quote size={80} className="text-[#FF3B3B]" />
            </div>

            {/* Content */}
            <div className="relative z-10">
              <p className="text-white text-xl md:text-2xl leading-relaxed mb-8 font-light italic">
                "{testimonials[currentIndex].content}"
              </p>

              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14 bg-[#FF3B3B]/20 border-2 border-[#FF3B3B]/30">
                  <AvatarFallback className="bg-[#FF3B3B]/20 text-[#FF3B3B] font-bold">
                    {testimonials[currentIndex].avatar}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="text-white font-semibold">{testimonials[currentIndex].name}</h4>
                  <p className="text-[#9CA3AF] text-sm">{testimonials[currentIndex].title}</p>
                </div>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-[#FF3B3B]/5 rounded-full blur-3xl" />
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-white hover:bg-[#FF3B3B] hover:border-[#FF3B3B] transition-all duration-300"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'w-8 bg-[#FF3B3B]' : 'bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-white hover:bg-[#FF3B3B] hover:border-[#FF3B3B] transition-all duration-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
